import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bug,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  Mail,
  MessageSquare,
  Search,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Toolbar } from '@/components/Toolbar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDateTime } from '@/lib/locale'
import { useContactMessages } from '@/features/platformAdmin/queries'
import type {
  ContactMessageCategory,
  ContactMessageRow,
  ContactMessageStatus,
} from '@/features/platformAdmin/types'

const PAGE_SIZE = 50

const CATEGORY_META: Record<ContactMessageCategory, { label: string; icon: LucideIcon }> = {
  sales:   { label: 'Talk to Sales',     icon: Mail },
  bug:     { label: 'Report an Issue',   icon: Bug },
  feature: { label: 'Request a Feature', icon: Lightbulb },
  support: { label: 'Get Help',          icon: HelpCircle },
}

const STATUS_LABEL: Record<ContactMessageStatus, string> = {
  requested: 'Requested',
  viewed: 'Viewed',
  in_progress: 'In progress',
  planned: 'Planned',
  already_available: 'Already in app',
  shipped: 'Shipped',
  wont_do: "Won't do",
  closed: 'Closed',
}

const statusVariant = (s: ContactMessageStatus) =>
  s === 'shipped' ? 'success'
    : s === 'in_progress' || s === 'planned' ? 'info'
      : s === 'wont_do' ? 'danger'
        : s === 'closed' || s === 'already_available' ? 'secondary'
          : s === 'viewed' ? 'info'
            : 'warning'

const fmtDate = (v: string | null) => (v ? formatDateTime(v) : '-')

export function ContactMessagesTab() {
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [status, setStatus] = useState<ContactMessageStatus | ''>('')
  const [category, setCategory] = useState<ContactMessageCategory | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const listQ = useContactMessages({
    status,
    category,
    search,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const rows = listQ.data?.messages ?? []
  const total = listQ.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1
  const toIdx = Math.min((page + 1) * PAGE_SIZE, total)

  const submitSearch = () => {
    setPage(0)
    setSearch(searchInput.trim())
  }

  const clearSearch = () => {
    setSearchInput('')
    setSearch('')
    setPage(0)
  }

  const open = (id: number) => navigate(`/platform-admin/contact-messages/${id}`)

  const columns: ColumnDef<ContactMessageRow>[] = [
    {
      accessorKey: 'created_at',
      header: 'When',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.created_at)}</span>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const meta = CATEGORY_META[row.original.category]
        const Icon = meta.icon
        return (
          <span className="inline-flex items-center gap-1.5 text-xs">
            <Icon className="size-3.5 text-muted-foreground" />
            {meta.label}
          </span>
        )
      },
    },
    {
      id: 'who',
      header: 'From',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <span className="font-medium">{row.original.name}</span>
          <span className="text-muted-foreground">{row.original.email}</span>
          {row.original.tenant_name ? (
            <span className="text-[10px] text-muted-foreground">
              Tenant: {row.original.tenant_name}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'subject',
      header: 'Subject',
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[360px] text-xs">
          {row.original.subject || row.original.description.slice(0, 80)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>
          {STATUS_LABEL[row.original.status]}
        </Badge>
      ),
    },
    {
      id: 'replies',
      header: 'Replies',
      cell: ({ row }) =>
        (row.original.reply_count ?? 0) > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs">
            <MessageSquare className="size-3.5 text-muted-foreground" />
            {row.original.reply_count}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => open(row.original.id)}>
            Open
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Submissions from the customer-facing &ldquo;Contact us&rdquo; form. Open a row to read
        the full message, reply to the customer, and change its status.
      </p>

      <Toolbar>
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Select
            value={status || 'all'}
            onValueChange={(v) => {
              setPage(0)
              setStatus(v === 'all' ? '' : (v as ContactMessageStatus))
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(Object.keys(STATUS_LABEL) as ContactMessageStatus[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={category || 'all'}
            onValueChange={(v) => {
              setPage(0)
              setCategory(v === 'all' ? '' : (v as ContactMessageCategory))
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="sales">Talk to Sales</SelectItem>
              <SelectItem value="bug">Report an Issue</SelectItem>
              <SelectItem value="feature">Request a Feature</SelectItem>
              <SelectItem value="support">Get Help</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSearch()
              }}
              placeholder="Search name, email, subject…"
              className="w-[260px] pl-8 pr-7"
            />
            {searchInput ? (
              <Button
                size="icon"
                variant="ghost"
                onClick={clearSearch}
                className="absolute right-1 top-1/2 size-7 -translate-y-1/2"
                aria-label="Clear search"
              >
                <X className="size-3.5" />
              </Button>
            ) : null}
          </div>
          <Button size="sm" variant="secondary" onClick={submitSearch}>
            Search
          </Button>
        </div>

        <span className="ml-auto text-xs text-muted-foreground">
          {total === 0 ? 'No messages.' : `Showing ${fromIdx}–${toIdx} of ${total}`}
        </span>
      </Toolbar>

      <Card>
        <CardContent className="p-3">
          {listQ.isLoading && rows.length === 0 ? (
            <LoadingSpinner />
          ) : listQ.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load contact messages.</AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState title="No contact submissions yet" />
          ) : (
            <DataTable columns={columns} rows={rows} />
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Page {page + 1} of {pageCount}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || listQ.isFetching}
          >
            <ChevronLeft className="size-4" />
            Prev
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page + 1 >= pageCount || listQ.isFetching}
          >
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

    </div>
  )
}
